import React, {useEffect, useState} from 'react';
import VeggieProgressImageComponent from './VeggieProgressImageComponent';
import fallbackImage from '../../../images/fallback/plantFallbackImage.png';
import '../../../css/gardenManagerStylings/plantsSection/GardenManagerPlantsStyling.css';

const GardenManagerPlantsComponent = ({ selectedGarden }) => {
    const [plants, setPlants] = useState([]);

    useEffect(() => {
        setPlants(selectedGarden?.plants || []);
    }, [selectedGarden]);

    // Auto-scroll implementation with hover control
    useEffect(() => {
        const cards = document.querySelectorAll('.horizontal-plant-status-card');
        const scrollers = [];

        cards.forEach((card) => {
            const container = card.querySelector('.status-card-footer');
            if (!container) return;

            let scrollAmount = 1; // Adjust speed (pixels per frame)
            let animationId = null;
            let isHovering = false;

            function autoScroll() {
                if (isHovering) {
                    container.scrollLeft += scrollAmount;

                    // Bounce at edges with small buffer to prevent jitter
                    if (container.scrollLeft + container.clientWidth >= container.scrollWidth - 2) {
                        scrollAmount = -Math.abs(scrollAmount);
                    } else if (container.scrollLeft <= 2) {
                        scrollAmount = Math.abs(scrollAmount);
                    }
                }

                animationId = requestAnimationFrame(autoScroll);
            }

            // Start the animation loop (always running but only scrolls on hover)
            animationId = autoScroll();
            scrollers.push(animationId);

            // Add hover event listeners
            const handleMouseEnter = () => {
                isHovering = true;
            };

            const handleMouseLeave = () => {
                isHovering = false;
            };

            card.addEventListener('mouseenter', handleMouseEnter);
            card.addEventListener('mouseleave', handleMouseLeave);

            // Store cleanup functions
            scrollers.push(() => {
                card.removeEventListener('mouseenter', handleMouseEnter);
                card.removeEventListener('mouseleave', handleMouseLeave);
            });
        });

        // Cleanup animation frames and event listeners on unmount
        return () => {
            scrollers.forEach(cleanup => {
                if (typeof cleanup === 'number') {
                    cancelAnimationFrame(cleanup);
                } else if (typeof cleanup === 'function') {
                    cleanup();
                }
            });
        };
    }, [plants]); // Re-run when plants change

    let sunlightMap = {
        'Full Sun': '6 +',
        'Partial Sun': '4 - 6',
        'Partial Shade': '3 - 4',
        'Full Shade': '< 3',
        'Filtered Light': '3 - 5'
    }

    const getSunHours = (sunlight) => {
        return sunlightMap[sunlight] || 'Unknown';
    }

    function generateAdvancedGradient(baseHex, type = 'analogous', intensity = 10) {
        // Convert hex to HSL
        const hexToHsl = (hex) => {
            const r = parseInt(hex.slice(1, 3), 16) / 255;
            const g = parseInt(hex.slice(3, 5), 16) / 255;
            const b = parseInt(hex.slice(5, 7), 16) / 255;
            const max = Math.max(r, g, b), min = Math.min(r, g, b);
            let h, s, l = (max + min) / 2;
            if (max === min) {
                h = s = 0;
            } else {
                const d = max - min;
                s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
                switch (max) {
                    case r: h = (g - b) / d + (g < b ? 6 : 0); break;
                    case g: h = (b - r) / d + 2; break;
                    case b: h = (r - g) / d + 4; break;
                }
                h /= 6;
            }
            return [h * 360, s * 100, l * 100];
        };

        // Convert HSL to hex
        const hslToHex = (h, s, l) => {
            h = ((h % 360) + 360) % 360;
            s = Math.max(0, Math.min(100, s)) / 100;
            l = Math.max(0, Math.min(100, l)) / 100;
            const c = (1 - Math.abs(2 * l - 1)) * s;
            const x = c * (1 - Math.abs((h / 60) % 2 - 1));
            const m = l - c / 2;
            let r, g, b;
            if (0 <= h && h < 60) { r = c; g = x; b = 0; }
            else if (60 <= h && h < 120) { r = x; g = c; b = 0; }
            else if (120 <= h && h < 180) { r = 0; g = c; b = x; }
            else if (180 <= h && h < 240) { r = 0; g = x; b = c; }
            else if (240 <= h && h < 300) { r = x; g = 0; b = c; }
            else { r = c; g = 0; b = x; }
            r = Math.round((r + m) * 255);
            g = Math.round((g + m) * 255);
            b = Math.round((b + m) * 255);
            return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
        };

        const [h, s, l] = hexToHsl(baseHex);
        let colors = [];

        const coolShift = l > 50 ? intensity : -intensity;
        colors = [
            hslToHex(h + 15, Math.max(30, s - 10), Math.max(20, l + coolShift)),
            baseHex,
            hslToHex(h - 15, Math.max(30, s), Math.min(80, l - coolShift))
        ];

        return colors;
    }

    return (
        <div className="garden-manager-plants-section">
            {plants?.map((plant, index) => {
                const colors = generateAdvancedGradient(plant.display_color);

                return (
                    <div key={index} className="horizontal-plant-status-card">
                        <div
                            className="plant-progress-container"
                            style={{
                                '--start-color': colors[0],
                                '--mid-color': colors[1],
                                '--end-color': colors[2]
                            }}
                        >
                            <VeggieProgressImageComponent
                                plantImageUrl={plant.plant.optimized_plant_image_url || fallbackImage}
                                plantName={plant.plant.plant_name}
                                plantedDate={plant.planted_date}
                                daysToMaturity={plant.plant.days_to_maturity}
                                plantStatus={plant.plant_current_stage_name ?? plant.plant_status}
                                size={200}
                            />
                        </div>

                        <div className={`status-card-main-body`}>
                            {/* header or plant name section */}
                            <div className={`status-card-header`}>
                                <div className={`status-card-plant-name`}>
                                    {plant.plant.plant_name}
                                </div>

                                <div className={`status-card-header-separator`}></div>

                                <div className={`status-card-plant-type`}>
                                    {plant.plant.plant_type}
                                </div>
                            </div>

                            <div className={`status-card-quad-section`}>
                                <div className={`status-card-quadrant`}>
                                    <div className={`status-card-quadrant-title`}>
                                        PLANTING DATE
                                    </div>
                                    <div className={`status-card-quadrant-value`}>
                                        {new Date(plant.planted_date).toLocaleString()}
                                    </div>
                                </div>

                                <div className={`status-card-quadrant`}>
                                    <div className={`status-card-quadrant-title`}>
                                        EXPECTED HARVEST
                                    </div>
                                    <div className={`status-card-quadrant-value`}>
                                        {new Date(plant.expected_harvest_date).toLocaleString()}
                                    </div>
                                </div>

                                <div className={`status-card-quadrant`}>
                                    <div className={`status-card-quadrant-title`}>
                                        CURRENT HEALTH
                                    </div>
                                    <div className={`status-card-quadrant-value`}>
                                        {plant.health_rating * 10}%
                                    </div>
                                </div>

                                <div className={`status-card-quadrant`}>
                                    <div className={`status-card-quadrant-title`}>
                                        CURRENT STATUS
                                    </div>
                                    <div className="status-card-quadrant-value">
                                        {plant.plant_current_stage_name ?? plant.plant_status}
                                        {plant.plant_current_stage_days_left != null && (
                                            <> — {plant.plant_current_stage_days_left} days left</>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className={`status-card-plant-tips`}>
                                <div className={`status-card-plant-tips-text`}>

                                </div>
                            </div>

                            <div className={`status-card-plant-tags`}>
                                {plant.plant.tags.map(tag => (
                                    <div className={`card-tag`}>
                                        {tag.toUpperCase()}
                                    </div>
                                ))}
                            </div>

                            <div className={`status-card-footer-separator`}></div>
                            <div className={`status-card-footer`}>
                                {/* Add auto-scroll class for CSS method alternative */}
                                <div className={`status-card-footer-body auto-scroll-container`}>

                                    <div className={`status-card-footer-section`}>
                                        <div className={`status-card-footer-section-value`}>
                                            pH {plant.plant.soil_ph}
                                        </div>
                                        <div className={`status-card-footer-section-title`}>
                                            SOIL PH
                                        </div>
                                    </div>

                                    <div className={`status-card-footer-section`}>
                                        <div className={`status-card-footer-section-value`}>
                                            {plant.plant.temperature_min}&deg; - {plant.plant.temperature_max}&deg;
                                        </div>
                                        <div className={`status-card-footer-section-title`}>
                                            TEMP RANGE
                                        </div>
                                    </div>

                                    <div className={`status-card-footer-section`}>
                                        <div className={`status-card-footer-section-value`}>
                                            {getSunHours(plant.plant.sunlight)}
                                        </div>
                                        <div className={`status-card-footer-section-title`}>
                                            SUN HOURS
                                        </div>
                                    </div>

                                    <div className={`status-card-footer-section`}>
                                        <div className={`status-card-footer-section-value`}>
                                            {plant.plant.water_frequency}x/week
                                        </div>
                                        <div className={`status-card-footer-section-title`}>
                                            WATERING
                                        </div>
                                    </div>

                                    <div className={`status-card-footer-section`}>
                                        <div className={`status-card-footer-section-value`}>
                                            {plant.plant.canopy_density}
                                        </div>
                                        <div className={`status-card-footer-section-title`}>
                                            CANOPY DENSITY
                                        </div>
                                    </div>

                                    <div className={`status-card-footer-section`}>
                                        <div className={`status-card-footer-section-value`}>
                                            {plant.plant.growth_speed}
                                        </div>
                                        <div className={`status-card-footer-section-title`}>
                                            GROWTH SPEED
                                        </div>
                                    </div>

                                    <div className={`status-card-footer-section`}>
                                        <div className={`status-card-footer-section-value`}>
                                            {plant.plant.drought_tolerance}
                                        </div>
                                        <div className={`status-card-footer-section-title`}>
                                            DROUGHT TOL.
                                        </div>
                                    </div>

                                    <div className={`status-card-footer-section`}>
                                        <div className={`status-card-footer-section-value`}>
                                            {plant.plant.shade_tolerance}
                                        </div>
                                        <div className={`status-card-footer-section-title`}>
                                            SHADE TOL.
                                        </div>
                                    </div>

                                    <div className={`status-card-footer-section`}>
                                        <div className={`status-card-footer-section-value`}>
                                            {plant.plant.humidity_tolerance}
                                        </div>
                                        <div className={`status-card-footer-section-title`}>
                                            HUMIDITY TOL.
                                        </div>
                                    </div>

                                    <div className={`status-card-footer-section`}>
                                        <div className={`status-card-footer-section-value`}>
                                            {plant.plant.heat_tolerance}
                                        </div>
                                        <div className={`status-card-footer-section-title`}>
                                            HEAT TOL.
                                        </div>
                                    </div>

                                    <div className={`status-card-footer-section`}>
                                        <div className={`status-card-footer-section-value`}>
                                            {plant.plant.frost_tolerance}
                                        </div>
                                        <div className={`status-card-footer-section-title`}>
                                            FROST TOL.
                                        </div>
                                    </div>

                                </div>

                            </div>
                        </div>

                    </div>
                )
            })}

            {plants.length === 0 && (
                // <div className={`status-card-no-plants-message`}>
                <div className={`no-entries-message`}>
                    <p>No plants to display...</p>
                </div>
            )}
        </div>
    )
}

export default GardenManagerPlantsComponent;